import React, { useEffect, useState } from 'react'
import { Popover, Box, Typography, Button, CircularProgress } from '@mui/material'
import {
  DEFAULT_SHARE_ACCESS_LEVEL,
  ShareAccessFields,
  ShareLinkField,
  sharePasswordMissing,
} from './ShareControls'

interface DashboardSharePopoverProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  shareId?: string
  shareAccessLevel?: string
  shareEnabled?: boolean
  onShare: (accessLevel: string, password?: string) => Promise<void>
  onDisableShare: () => Promise<void>
  isLoading?: boolean
}

/**
 * Per-row sharing popover for the dashboards homepage (#962) — the issue's
 * decided alternative to an inline row field, which would crowd the row.
 * The same controls as the Sharing tab of dashboard settings (ShareControls),
 * without the embed code, so it fits in a popover.
 */
const DashboardSharePopover: React.FC<DashboardSharePopoverProps> = ({
  anchorEl,
  onClose,
  shareId,
  shareAccessLevel,
  shareEnabled,
  onShare,
  onDisableShare,
  isLoading,
}) => {
  const [accessLevel, setAccessLevel] = useState<string>(shareAccessLevel || DEFAULT_SHARE_ACCESS_LEVEL)
  const [password, setPassword] = useState('')
  const hasPassword = Boolean(shareEnabled && shareAccessLevel === 'password')

  useEffect(() => {
    if (anchorEl) {
      setAccessLevel(shareAccessLevel || DEFAULT_SHARE_ACCESS_LEVEL)
      setPassword('')
    }
  }, [anchorEl, shareAccessLevel])

  const handleShare = async () => {
    await onShare(accessLevel, accessLevel === 'password' ? password : undefined)
    setPassword('')
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ p: 2, width: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle2">Sharing</Typography>

        <ShareAccessFields
          accessLevel={accessLevel}
          onAccessLevelChange={setAccessLevel}
          password={password}
          onPasswordChange={setPassword}
          hasPassword={hasPassword}
        />

        <Button
          variant="contained"
          color="info"
          size="small"
          onClick={handleShare}
          disabled={isLoading || sharePasswordMissing(accessLevel, password, hasPassword)}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {shareEnabled ? 'Update Sharing' : 'Publish'}
        </Button>

        {shareEnabled && shareId && (
          <ShareLinkField shareId={shareId} onDisable={onDisableShare} disabled={isLoading} />
        )}
      </Box>
    </Popover>
  )
}

export default DashboardSharePopover
